import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Home, 
  QrCode, 
  Inbox, 
  Search, 
  User,
  History,
  CheckCircle2,
  Settings
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Capacitor } from '@capacitor/core';

const BottomNavigation: React.FC = () => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const isNative = Capacitor.isNativePlatform();
  
  // Hide bottom nav on sender upload, scanner, tracking, and auth pages
  const hidePaths = ['/login', '/register', '/forgot-pin', '/registration-success', '/send', '/track'];
  if (hidePaths.includes(location.pathname) || location.pathname.startsWith('/shop/') || location.pathname.startsWith('/track')) {
    return null;
  }

  // Role detection
  const isShopkeeper = user?.is_shopkeeper || false;
  const homePath = isNative ? (isShopkeeper ? '/dashboard' : '/send') : '/';

  // 1. Guest Navigation (Not Logged In)
  const guestItems = [
    { label: 'Home', path: homePath, icon: <Home className="w-5 h-5" /> },
    { label: 'Find', path: '/find-shops', icon: <Search className="w-5 h-5" /> },
    { label: 'Scan', path: '/send', icon: <QrCode className="w-5 h-5" /> },
    { label: 'Login', path: '/login', icon: <User className="w-5 h-5" /> },
  ];

  // 2. Sender Navigation (Customer)
  const senderItems = [
    { label: 'Home', path: homePath, icon: <Home className="w-5 h-5" /> },
    { label: 'Search', path: '/find-shops', icon: <Search className="w-5 h-5" /> },
    { label: 'Scan', path: '/send', icon: <QrCode className="w-5 h-5" /> },
    { label: 'Prints', path: '/track', icon: <History className="w-5 h-5" /> },
    { label: 'Profile', path: '/profile', icon: <User className="w-5 h-5" /> },
  ];

  // 3. Shopkeeper Navigation
  const shopkeeperItems = [
    { label: 'Inbox', path: '/dashboard', tab: 'inbox', icon: <Inbox className="w-5 h-5" /> },
    { label: 'Printed', path: '/dashboard', tab: 'printed', icon: <CheckCircle2 className="w-5 h-5" /> },
    { label: 'QR', path: '/profile', tab: 'qr', icon: <QrCode className="w-5 h-5" /> },
    { label: 'Settings', path: '/profile', tab: 'profile', icon: <Settings className="w-5 h-5" /> },
  ];

  const navItems = !isAuthenticated ? guestItems : (isShopkeeper ? shopkeeperItems : senderItems);

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-[100] bg-white/80 backdrop-blur-lg border-t border-gray-100 pb-safe-bottom md:hidden shadow-[0_-4px_20px_rgba(0,0,0,0.03)]">
      <div className="flex items-center justify-around h-16 max-w-lg mx-auto px-4">
        {navItems.map((item) => {
          const searchParams = new URLSearchParams(location.search);
          const activeTab = searchParams.get('tab');
          const isItemActive = 
            location.pathname === item.path && 
            (!item.tab || (activeTab === item.tab) || (!activeTab && (item.tab === 'inbox' || item.tab === 'profile')));

          return (
            <NavLink
              key={item.path + item.label}
              to={item.tab ? `${item.path}?tab=${item.tab}` : item.path}
              className={`
                flex flex-col items-center justify-center min-w-[64px] h-full transition-all duration-300
                ${isItemActive && !item.isAction ? 'text-blue-600 scale-110' : 'text-gray-400 hover:text-gray-600'}
                ${item.isAction ? 'relative' : ''}
              `}
            >
              {item.isAction ? (
                <div className="flex flex-col items-center -mt-8">
                  <div className={`
                    p-4 rounded-2xl shadow-xl shadow-blue-500/30 transition-transform active:scale-90
                    ${isItemActive ? 'bg-blue-600' : 'bg-gradient-to-tr from-blue-600 to-indigo-600'}
                  `}>
                    {item.icon}
                  </div>
                  <span className={`text-[10px] font-black uppercase tracking-widest mt-2 ${isItemActive ? 'text-blue-600' : 'text-gray-400'}`}>
                    {item.label}
                  </span>
                </div>
              ) : (
                <>
                  <div className="mb-1">
                    {item.icon}
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-tight">
                    {item.label}
                  </span>
                  {isItemActive && (
                    <motion.div 
                      layoutId="nav-dot"
                      className="w-1 h-1 bg-blue-600 rounded-full mt-0.5"
                    />
                  )}
                </>
              )}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNavigation;
